import { useCallback, useEffect, useState } from "react";

import {
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  sub,
  subMonths,
  subYears,
} from "date-fns";
import {
  generatePath,
  Redirect,
  useHistory,
  useRouteMatch,
} from "react-router-dom";

import { getCapabilities } from "@/api/endpoints/organization";
import { deleteReport } from "@/api/endpoints/reports";
import { getSearchList } from "@/api/endpoints/searches";
import { postTrackingData } from "@/api/endpoints/track";
import fetcher_v2 from "@/api/fetcher_v2";
import { URLS } from "@/api/urls";
import CalendarIcon from "@/assets/icons/calendar.svg";
import DotsHorizontal from "@/assets/icons/dots-horizontal.svg";
import { config } from "@/config";
import { useTags } from "@/contexts/TagContext";
import { useOrganization } from "@/hooks/useOrganization";
import usePrevious from "@/hooks/usePrevious";
import { ShareIcon } from "@/Icons";
import { ROUTES } from "@/routes_new";
import { EVENT } from "@/tracking_events";
import { cx, ensureIsDate, formatDateForApi, stringifyUrl } from "@/utils";
import { getWidgetSize } from "@/views/reports/layout";
import AccessControl, { ACCESS_LEVELS } from "@components/AccessControl";
import AllEarsTitle from "@components/AllEarsTitle";
import Button from "@components/Button";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "@components/Dropdown";

import { AEDatePicker, AEDatePickerForTimeComparison } from "../AEDatePicker";
import { EditableField } from "../EditableField";
import { EditWidgetModal } from "../EditWidgetModal";
import { mirrorWidgetsForTimeComparisonReport } from "../helpers.js";
import { MentionsModal } from "../MentionsModal";
import { ScheduleModal } from "../ScheduleModal";
import NewWidgetButton from "./NewWidgetButton";
import ScheduleButton from "./ScheduleButton";
import WidgetList from "./WidgetList";

import styles from "./ReportBuilder.module.css";
import { i18n } from "@lingui/core";

// converts daily/monthly => day/month
const INTERVALS_FOR_TIME_COMPARISON = {
  daily: L.REPORTS_TIME_COMPARISON_INTERVALS_DAILY,
  weekly: L.REPORTS_TIME_COMPARISON_INTERVALS_WEEKLY,
  monthly: L.REPORTS_TIME_COMPARISON_INTERVALS_MONTHLY,
  quarterly: L.REPORTS_TIME_COMPARISON_INTERVALS_QUARTERLY,
  yearly: L.REPORTS_TIME_COMPARISON_INTERVALS_YEARLY,
};

const intervalToTimePeriod = (interval) =>
  interval === "daily" ? "day" : interval.replace(/ly$/, "");

const startOf = (date, interval) => {
  if (interval === "day") {
    return startOfDay(date);
  }
  if (interval === "week") {
    return startOfWeek(date);
  }
  if (interval === "month") {
    return startOfMonth(date);
  }
  if (interval === "quarter") {
    return startOfQuarter(date);
  }
  if (interval === "year") {
    return startOfYear(date);
  }
};
const endOf = (date, interval) => {
  if (interval === "day") {
    return endOfDay(date);
  }
  if (interval === "week") {
    return endOfWeek(date);
  }
  if (interval === "month") {
    return endOfMonth(date);
  }
  if (interval === "quarter") {
    return endOfQuarter(date);
  }
  if (interval === "year") {
    return endOfYear(date);
  }
};

const ReportBuilder = (props) => {
  const [loaded, setLoaded] = useState(props.isNewReport || false);
  const [uuid, setUuid] = useState(null); // Used in edit mode
  const [title, setTitle] = useState(L.REPORTS_REPORT_TITLE_PLACEHOLDER);
  const [interval, setInterval] = useState("daily");
  const [widgets, setWidgets] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [alertSubscriptions, setAlertSubscriptions] = useState(null);
  const { organizationTags } = useTags();
  const [templateName, setTemplateName] = useState();

  // date picker end date
  const [endDate, setEndDate] = useState(new Date());

  // set default start date to 6 months ago (same as live search default)
  const [startDate, setStartDate] = useState(subMonths(new Date(), 6));

  const [redirectUrl, setRedirectUrl] = useState(null);

  const [saveTrigger, setSaveTrigger] = useState(0);
  const [reloadWidgets, setReloadWidgets] = useState(true);

  const [showNewWidgetOverlay, setShowNewWidgetOverlay] = useState(false);
  const [widgetToEdit, setWidgetToEdit] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(
    props.showScheduleModalOnInit || false
  );

  const [_alertMessage, setAlertMessage] = useState(null);

  // MentionsModal
  const [showMentionsModal, setShowMentionsModal] = useState(false);
  const [fetchMentionsParams, setFetchMentionsParams] = useState({});

  const fetchMentions = (sources, filters = {}) => {
    if (!filters.dates) filters.dates = [startDate, endDate];

    setFetchMentionsParams({
      report: uuid,
      sources: sources,
      filters: filters,
      timestamp: Date.now(), // to trigger a new fetch
    });

    setShowMentionsModal(true);
  };

  const history = useHistory();

  const [compareStartDate, setCompareStartDate] = useState();
  const [compareEndDate, setCompareEndDate] = useState();

  const { data: orgData } = useOrganization();
  const { uuid: orgUuid, users: organizationUsers = [] } = orgData?.ok
    ? orgData.data
    : {};

  useEffect(() => {
    if (templateName !== "time_comparison") return;

    // convert "daily" to "day" so we can use it
    const timePeriod = intervalToTimePeriod(interval);

    const newStartDate = startOf(ensureIsDate(startDate), timePeriod);
    const newEndDate = endOf(ensureIsDate(startDate), timePeriod);

    if (startDate.getTime() !== newStartDate.getTime()) {
      setStartDate(newStartDate);
      setEndDate(newEndDate);
    }

    // base comparison date on previous comparison date and fallback to startDate
    let start = sub(ensureIsDate(startDate), { [`${timePeriod}s`]: 1 });
    setCompareStartDate(startOf(start, timePeriod));
    setCompareEndDate(endOf(start, timePeriod));
  }, [interval, startDate, templateName]);

  useEffect(() => {
    if (orgUuid && uuid !== null) {
      history.push(generatePath(ROUTES.REPORTS_EDIT_ORGID, { orgUuid, uuid }));
    }
  }, [uuid, orgUuid, history]);

  const urlUuid = useRouteMatch(ROUTES.REPORTS_EDIT_ORGID)?.params?.uuid;
  useEffect(() => {
    if (urlUuid) {
      setUuid(urlUuid);
    }
  }, [urlUuid]);

  useEffect(() => {
    getSearchList().then(({ data }) => setAlertSubscriptions(data));
  }, []);

  const maybeChangeStartDate = useCallback((date) => {
    const d = ensureIsDate(date);
    setStartDate((current) => (ensureIsDate(current) < d ? d : current));
  }, []);
  useEffect(() => {
    if (urlUuid !== null && urlUuid !== undefined && startDate && endDate) {
      // fetch new data because dates were changed
      fetcher_v2(`${config.API_URL}/reports/${urlUuid}/`)
        .then(({ ok, statusText, data }) => {
          if (!ok) {
            throw Error(statusText);
          }
          return data;
        })
        .then((data) => {
          setUuid(data.uuid);
          setTitle(data.title);
          setSubscribers(data.subscribers);
          setWidgets(data.widgets);
          setTemplateName(data.template);
          if (data.template === "social_media_overview") {
            setStartDate(subMonths(new Date(), 1));
          }
          maybeChangeStartDate(data.start_date);

          // date must be set before setting interval
          setInterval(data.interval);
          setLoaded(true);
        })
        .catch((error) => {
          document.body.innerHTML = `<div class="error"
          style="padding: 1rem; text-align: center">${String(error)}</div>`;
          throw error;
        });
    }
  }, [endDate, startDate, urlUuid, maybeChangeStartDate]);

  useEffect(() => {
    document.body.classList.toggle("overview", false);
    document.body.classList.toggle("report_builder", true);
  }, []);

  // get available markets for current organization
  const [userMarkets, setUserMarkets] = useState();

  useEffect(() => {
    if (!orgUuid) return;
    getCapabilities(orgUuid).then(({ data }) => setUserMarkets(data.markets));
  }, [orgUuid]);

  const [, setValidated] = useState(false);

  const validateForm = (form) => {
    setValidated(true);
    return form.checkValidity() && widgets.length > 0;
  };

  const onTitleChange = (e) => {
    validateForm(e.target.form);
    setTitle(e.target.value);
  };

  const saveReport = () => {
    const form = document.getElementById("reportForm");

    if (!validateForm(form)) {
      setAlertMessage({
        variant: "danger",
        message: L.REPORTS_ALERT_NOT_SAVED,
      });

      return;
    }

    // Send no UUID on widgets if they are newly created,
    // the UUID should be set by the backend on creation
    const filteredWidgets = widgets.map(({ uuid, ...restData }) => {
      return uuid === "" ? restData : { uuid, ...restData };
    });

    const data = {
      title,
      interval,
      subscribers,
      widgets: filteredWidgets,
    };

    const isNewReport = !uuid;
    let path;
    if (isNewReport) {
      path = URLS.REPORT_LIST;
    } else {
      path = generatePath(URLS.REPORT, { uuid });
    }

    // if we're reloading widgets later then show their loading spinners directly
    // TODO: Fix Direct mutation of state here:
    if (reloadWidgets) {
      setWidgets(
        [...widgets].map((w) => {
          delete w.data;
          return w;
        })
      );
    }

    fetcher_v2(path, {
      method: isNewReport ? "POST" : "PUT",
      body: data,
    })
      .then(({ data }) => {
        // setAlertMessage({ variant: "success", message: L.REPORTS_ALERT_SAVED });
        setUuid(data.uuid);

        if (isNewReport || reloadWidgets) {
          setWidgets(data.widgets);
        }

        // always reset to true to prevent undesired side effects
        setReloadWidgets(true);
      })
      .catch((error) => {
        setAlertMessage({
          variant: "danger",
          message: L.REPORTS_ALERT_NOT_SAVED,
        });
        throw error;
      });
  };

  useEffect(() => {
    if (!loaded) return;
    saveReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interval, subscribers, loaded]);

  useEffect(() => {
    // no need to save 1st time
    if (saveTrigger > 0) {
      saveReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveTrigger]);

  const save = () => {
    setSaveTrigger(saveTrigger + 1);
  };

  const onAddWidget = (widget) => {
    const newWidgets = widgets
      .concat([widget])
      .sort((a, b) => a.sorting_position - b.sorting_position);

    setWidgets(newWidgets);
    save();
  };

  const onDeleteWidget = (widget) => {
    const newWidgets = widgets
      .filter((w) => w !== widget)
      .sort((a, b) => a.sorting_position - b.sorting_position);

    setWidgets(newWidgets);
    save();
  };

  const onEditWidget = (widget) => {
    const widgetToChange = widgets.find((w) => w.uuid === widget.uuid);
    widgetToChange.title = widget.title;
    widgetToChange.metric = widget.metric;
    widgetToChange.chart_type = widget.chart_type;
    widgetToChange.searches = widget.searches;
    widgetToChange.tags = widget.tags;
    widgetToChange.sorting_position = widget.sorting_position;
    widgetToChange.is_compact = widget.is_compact;
  };

  const prevTitle = usePrevious(title);

  const onTitleBlur = () => {
    if (prevTitle !== title) {
      setReloadWidgets(false);
      save();
    }
  };

  const onSaveWidgetButtonClick = (newWidget) => {
    onAddWidget(newWidget);
    setShowNewWidgetOverlay(!showNewWidgetOverlay);
  };

  const onEditWidgetButtonClick = () => {
    setShowNewWidgetOverlay(!showNewWidgetOverlay);
  };

  const onSaveEditedWidget = (editedWidget) => {
    onEditWidget(editedWidget);
    setShowNewWidgetOverlay(!showNewWidgetOverlay);
    save();
  };

  const toggleNewWidgetView = () => {
    setShowNewWidgetOverlay(!showNewWidgetOverlay);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };
  const onScheduleModalHide = () => {
    setShowScheduleModal(false);
  };

  const handleDeleteReport = () => {
    // TODO: Use better looking confirmation such as Modal
    if (confirm(L.REPORTS_REPORT_DELETE_CONFIRM)) {
      deleteReport(uuid)
        .then((response) => {
          if (response.ok) {
            setRedirectUrl(generatePath(ROUTES.REPORTS_ORGID, { orgUuid }));
          }
        })
        .catch((error) => {
          throw error;
        });
    }
  };

  if (redirectUrl) {
    return <Redirect to={redirectUrl} />;
  }

  const setDatePreset = (value, unit) => {
    if (value === "today") {
      setStartDate(new Date());
      setEndDate(new Date());
    } else if (value === "prev_year") {
      setStartDate(startOfYear(subYears(new Date(), 1)));
      setEndDate(endOfYear(subYears(new Date(), 1)));
    } else if (value === "ytd") {
      setStartDate(startOfYear(new Date()));
      setEndDate(new Date());
    } else {
      setStartDate(sub(new Date(), { [unit]: value }));
      setEndDate(new Date());
    }
  };

  return (
    <>
      <AllEarsTitle title={title} />

      <div
        className={cx(props.className, `template-${templateName}`, {
          [styles.timeComparison]: templateName === "time_comparison",
        })}
        data-testid={
          uuid
            ? loaded
              ? "existing-report-loaded"
              : "existing-report-loadeding"
            : "new-report"
        }
      >
        <ScheduleModal
          show={showScheduleModal}
          onHide={onScheduleModalHide}
          interval={interval}
          setInterval={setInterval}
          users={organizationUsers}
          subscribers={subscribers}
          setSubscribers={setSubscribers}
          template={templateName}
        />

        <div>
          <div className={styles.toolbar}>
            <ScheduleButton
              subscribers={subscribers}
              onClick={() => setShowScheduleModal(true)}
              className="md:mr-2"
            />

            {templateName === "time_comparison" ? (
              <Dropdown>
                <DropdownToggle asChild>
                  <Button
                    className="dropdown-button"
                    size="sm"
                    variant="secondary-grayish"
                    icon={CalendarIcon}
                  >
                    {i18n._(INTERVALS_FOR_TIME_COMPARISON[interval])}
                  </Button>
                </DropdownToggle>
                <DropdownMenu>
                  {Object.entries(INTERVALS_FOR_TIME_COMPARISON).map(
                    ([k, v]) => (
                      <DropdownItem onClick={() => setInterval(k)} key={k}>
                        {i18n._(v)}
                      </DropdownItem>
                    )
                  )}
                </DropdownMenu>
              </Dropdown>
            ) : (
              <AEDatePicker
                selected={startDate}
                onChange={([start, end]) => {
                  setStartDate(start);
                  setEndDate(end);
                }}
                startDate={startDate}
                endDate={endDate}
                maxDate={new Date()}
                monthsShown={2}
                selectsRange
                shouldCloseOnSelect={!endDate}
                dateFormat="yyyy-MM-dd"
                className="md:ml-1"
                setDatePreset={setDatePreset}
                popperPlacement="bottom-start"
              />
            )}

            <Button
              variant="secondary"
              size="sm"
              href={
                uuid
                  ? stringifyUrl({
                      url: generatePath(ROUTES.MENTION_INSIGHTS_SHARE, {
                        report_uuid: uuid,
                      }),
                      query: {
                        start_date: formatDateForApi(new Date(startDate)),
                        end_date: formatDateForApi(new Date(endDate)),
                        compare_start_date:
                          compareStartDate &&
                          formatDateForApi(new Date(compareStartDate)),
                        compare_end_date:
                          compareEndDate &&
                          formatDateForApi(new Date(compareEndDate)),
                      },
                    })
                  : undefined
              }
              target="_blank"
              disabled={uuid == ""}
              onClick={() => {
                postTrackingData(EVENT.SHARE_REPORT, {
                  uuid: uuid,
                  report: title,
                  template: templateName,
                });
              }}
              className="share-button md:mr-2 md:ml-auto"
              icon={ShareIcon}
            >
              {L.REPORTS_REPORT_SHARE}
            </Button>

            <AccessControl level={ACCESS_LEVELS.USER}>
              <Dropdown>
                <DropdownToggle asChild>
                  <Button
                    className="dropdown-button"
                    size="sm"
                    disabled={uuid == ""}
                    variant="secondary-grayish"
                  >
                    <DotsHorizontal width="14" className="md:mr-2" />
                  </Button>
                </DropdownToggle>
                <DropdownMenu>
                  <DropdownItem
                    onClick={handleDeleteReport}
                    className="text-danger"
                  >
                    {L.REPORTS_REPORT_DELETE}
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </AccessControl>
          </div>
        </div>

        <div>
          {/* Report info */}
          <div>
            <form noValidate id="reportForm" onSubmit={handleSubmit}>
              <AccessControl visible>
                <EditableField
                  placeholder={L.REPORTS_REPORT_TITLE_PLACEHOLDER}
                  onChange={onTitleChange}
                  onBlur={onTitleBlur}
                  value={title}
                  required
                  disabled={!loaded}
                  autoFocus={props.data === null}
                  className="inline-editable h1-bold text-center mt-3"
                  data-testid="report-title"
                />
              </AccessControl>
            </form>
          </div>
        </div>

        {templateName === "time_comparison" && (
          <div className="flex">
            <div className="flex flex-1 justify-center">
              <AEDatePickerForTimeComparison
                selected={compareStartDate}
                onChange={(v) => {
                  setCompareStartDate(v);
                  setCompareEndDate(
                    endOf(new Date(v), intervalToTimePeriod(interval))
                  );
                }}
                timePeriod={intervalToTimePeriod(interval)}
              />
            </div>

            <div className="md:hidden text-center mt-2 mb-3">
              {L.REPORTS_TIME_COMPARISON_COMPARED_WITH}
            </div>

            <div className="flex flex-1 justify-center">
              <AEDatePickerForTimeComparison
                selected={startDate}
                onChange={(v) => {
                  setStartDate(v);
                  setEndDate(
                    endOf(new Date(v), intervalToTimePeriod(interval))
                  );
                }}
                timePeriod={intervalToTimePeriod(interval)}
              />
            </div>
          </div>
        )}

        {/* List widgets */}
        {loaded && (
          <div
            className={styles.widgetContainer}
            data-testid="widget-container"
          >
            <WidgetList
              reportUuid={uuid}
              templateName={templateName}
              interval={interval}
              widgets={
                templateName === "time_comparison"
                  ? mirrorWidgetsForTimeComparisonReport(
                      widgets,
                      compareStartDate,
                      compareEndDate,
                      true
                    )
                  : widgets
              }
              onEditWidgetButtonClick={onEditWidgetButtonClick}
              onSaveEditedWidget={onSaveEditedWidget}
              setWidgetToEdit={setWidgetToEdit}
              startDate={startDate}
              endDate={endDate}
              fetchMentions={fetchMentions}
            />

            <NewWidgetButton
              size={getWidgetSize(
                templateName,
                widgets.length + 1,
                widgets.length
              )}
              onClick={toggleNewWidgetView}
              templateName={templateName}
              data-testid="new-widget-button"
            />
          </div>
        )}

        {/* New/edit widget modal */}
        <EditWidgetModal
          show={showNewWidgetOverlay}
          onBackButtonClick={toggleNewWidgetView}
          onSaveWidgetButtonClick={onSaveWidgetButtonClick}
          onSaveEditedWidget={onSaveEditedWidget}
          onDeleteWidget={onDeleteWidget}
          searches={alertSubscriptions}
          tags={organizationTags}
          setWidgetToEdit={setWidgetToEdit}
          widgetToEdit={widgetToEdit}
          editMode={widgetToEdit !== null}
          organizationMarkets={userMarkets}
        />

        {/* Modal to show mentions when clicking in the charts */}
        <MentionsModal
          show={showMentionsModal}
          onHide={() => setShowMentionsModal(false)}
          params={fetchMentionsParams}
        />
      </div>
    </>
  );
};

export { ReportBuilder };
